import { AnimatedMessageContent } from "@/components/CaseChat/AnimatedMessageContent";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ChatMessage } from "@/services/caseChatServiceV2.types";
import { cva } from "class-variance-authority";
import { MessageSquare, Send, SquarePen, X } from "lucide-react";
import type { RefObject } from "react";

const messageVariants = cva(
  "max-w-[85%] break-words rounded-xl px-3 py-2 text-sm leading-[1.45] animate-message-in",
  {
    variants: {
      role: {
        user: "self-end rounded-br-[0.125rem] bg-primary-active text-primary-foreground whitespace-pre-wrap [box-shadow:0_1px_2px_hsl(var(--primary-active)/0.25)]",
        assistant: "self-start rounded-bl-[0.125rem] bg-muted text-foreground",
      },
    },
  }
);

interface CaseChatPanelViewProps {
  containerStyle: React.CSSProperties;
  panelRef: RefObject<HTMLDivElement | null>;
  scrollAreaRef: RefObject<HTMLDivElement | null>;
  messagesContainerRef: RefObject<HTMLDivElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  title: string | null;
  onClose: () => void;
  onResizeStart: (e: React.PointerEvent<HTMLDivElement>) => void;
  onNewConversation: () => void;
  isNewConversationDisabled: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  question: string;
  onQuestionChange: (question: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const CaseChatPanelView = ({
  containerStyle,
  panelRef,
  scrollAreaRef,
  messagesContainerRef,
  textareaRef,
  title,
  onClose,
  onResizeStart,
  onNewConversation,
  isNewConversationDisabled,
  messages,
  isStreaming,
  isLoadingHistory,
  error,
  question,
  onQuestionChange,
  onKeyDown,
  onSubmit,
}: CaseChatPanelViewProps) => {
  return (
    <div
      ref={panelRef}
      style={containerStyle}
      className="flex animate-panel-in flex-col overflow-visible border-l border-border bg-card [box-shadow:-2px_0_8px_-2px_rgba(0,0,0,0.06),-12px_0_32px_-8px_rgba(0,0,0,0.08)]"
    >
      <div
        onPointerDown={onResizeStart}
        className="absolute -left-2 bottom-0 top-0 z-10 w-5 cursor-col-resize touch-none after:absolute after:bottom-0 after:left-1/2 after:top-0 after:w-[3px] after:-translate-x-1/2 after:rounded-full after:bg-border after:[transition:background-color_0.15s_ease,width_0.15s_ease] hover:after:w-1 hover:after:bg-primary/[0.55] active:after:w-1 active:after:bg-primary/80"
      />
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 [background:linear-gradient(180deg,hsl(var(--primary-subtle)/0.5)_0%,transparent_100%)]">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary-subtle text-primary">
              <MessageSquare size={15} />
            </span>
            <h3 className="m-0 truncate text-sm font-semibold tracking-[-0.01em] text-foreground">
              {title || "Case Chat"}
            </h3>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onNewConversation}
              disabled={isNewConversationDisabled}
              className="inline-flex cursor-pointer items-center gap-[0.3125rem] rounded-full border border-primary/35 bg-primary-subtle/60 px-2.5 py-[0.3125rem] text-xs font-semibold text-primary [transition:all_0.15s_ease] hover:bg-primary-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:text-muted-foreground"
            >
              <SquarePen size={14} />
              <span>New chat</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-muted-foreground [transition:all_0.15s_ease] hover:border-muted-foreground/40 hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <ScrollArea
          className="min-h-0 flex-1 [&_[data-radix-scroll-area-thumb]:hover]:bg-primary/40 [&_[data-radix-scroll-area-thumb]]:bg-primary/25"
          ref={scrollAreaRef}
        >
          {isLoadingHistory ? (
            <div className="flex h-full flex-col items-center justify-center gap-3.5 p-8 text-center text-sm text-muted-foreground">
              Loading conversation...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3.5 p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-subtle text-primary">
                <MessageSquare size={22} />
              </span>
              <p className="max-w-[22rem] text-sm text-muted-foreground">
                Ask a question about this case's documents to get started.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 p-4" ref={messagesContainerRef}>
              {messages.map((message, index) => {
                const isLiveAssistantMessage =
                  index === messages.length - 1 && message.role === "assistant" && isStreaming;
                const isPendingAssistant = isLiveAssistantMessage && !message.content;
                return (
                  <div key={message.message_id} className={messageVariants({ role: message.role })}>
                    {isPendingAssistant ? (
                      <span
                        className="flex items-center gap-1 py-1"
                        aria-label="Assistant is typing"
                      >
                        <span className="h-1.5 w-1.5 animate-typing rounded-full bg-muted-foreground/60" />
                        <span className="h-1.5 w-1.5 animate-typing rounded-full bg-muted-foreground/60 [animation-delay:0.15s]" />
                        <span className="h-1.5 w-1.5 animate-typing rounded-full bg-muted-foreground/60 [animation-delay:0.3s]" />
                      </span>
                    ) : message.role === "assistant" ? (
                      <AnimatedMessageContent
                        content={message.content}
                        isLive={isLiveAssistantMessage}
                      />
                    ) : (
                      message.content
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {error && (
          <div className="border-t border-destructive/20 bg-destructive/[0.06] px-4 py-2 text-dense text-destructive">
            {error}
          </div>
        )}

        <form
          className="flex items-end gap-2 border-t border-border bg-card px-4 py-3"
          onSubmit={onSubmit}
        >
          <textarea
            ref={textareaRef}
            value={question}
            onChange={(e) => onQuestionChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about this case's documents..."
            rows={2}
            disabled={isStreaming}
            className="max-h-32 flex-1 resize-none overflow-y-auto rounded-lg border border-border bg-background px-3 py-2 font-[inherit] text-sm leading-[1.4] text-foreground [transition:border-color_0.15s_ease,box-shadow_0.15s_ease] placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:[box-shadow:0_0_0_3px_hsl(var(--primary)/0.12)] disabled:cursor-not-allowed disabled:bg-muted"
          />
          <button
            type="submit"
            disabled={isStreaming || !question.trim()}
            aria-label="Send message"
            className="flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-primary text-primary-foreground [transition:background-color_0.15s_ease,transform_0.1s_ease] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary enabled:hover:bg-primary-hover enabled:active:scale-press disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};
